import { useState, useRef, useCallback, useEffect } from 'react';
import styles from './Tooltip.module.css';
import type { TooltipProps } from './Tooltip.types';

export function Tooltip({
  content,
  shortcut,
  children,
  position = 'top',
  delay = 400,
  disabled = false,
  className,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const showTooltip = useCallback(() => {
    if (disabled) return;

    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [delay, disabled]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Hide on scroll
  useEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => hideTooltip();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isVisible, hideTooltip]);

  const tooltipClasses = [styles.tooltip, styles[position], isVisible && styles.visible]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className ?? ''}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      <div className={tooltipClasses} role="tooltip" aria-hidden={!isVisible}>
        <div className={styles.content}>
          <span className={styles.text}>{content}</span>
          {shortcut && <kbd className={styles.shortcut}>{shortcut}</kbd>}
        </div>
      </div>
    </div>
  );
}
